import { getConfig } from './config';

export interface Override {
  testerAlias: string;
  addedTags: string[];
  removedTags: string[];
  includeInScheduling: boolean;
  priorityScore: number;
  // Free-text note explaining why the override exists. UI-only; not used by the
  // scheduling engine. Empty when unset.
  comment: string;
  // Audit trail, stamped server-side on every write. ISO 8601 UTC timestamp and
  // the alias of the user who last saved the override. Empty for legacy rows.
  lastModifiedAt: string;
  lastModifiedBy: string;
  // First-line (direct) manager login, resolved via PAPI. Empty when unknown.
  directManager: string;
  // AppSec pillar whose practice the tester belongs to (e.g.
  // 'software_services'). Empty when the tester is in no known pillar practice.
  pillar: string;
}

// The signed-in user, echoed back by GET /overrides so the UI can default the
// "only my direct reports" filter.
export interface CurrentUser {
  alias: string;
}

export interface OverridesResponse {
  overrides: Override[];
  currentUser: CurrentUser;
}

export interface Prompt {
  key: string;
  label: string;
  description: string;
  placeholders: string[];
  default: string;
  content: string;
  isDefault: boolean;
  // The pillar a per-pillar assignment-rules prompt belongs to (e.g.
  // 'software_services'). null for shared prompts (system prompt, user template).
  pillar?: string | null;
  // Whether the requesting user is allowed to edit this prompt. When false the
  // editor is rendered read-only.
  canEdit?: boolean;
  updatedAt?: string | null;
  updatedBy?: string | null;
}

export interface PromptVersion {
  versionId: string;
  // Human-friendly, monotonic version number (1, 2, 3, ...). May be null for
  // legacy history rows recorded before numbering existed.
  versionNumber?: number | null;
  content: string;
  isDefault: boolean;
  updatedAt?: string | null;
  updatedBy?: string | null;
}

export interface SchedulingThread {
  threadUuid: string;
  avpId: string;
  title: string;
  // The classified pillar, e.g. 'software_services'. Empty when unclassified.
  pillar: string;
  // Login of the pentest manager responsible for the pillar. Empty when
  // the pillar is unclassified or has no assignable practice.
  pillarManager: string;
  status: 'assigned' | 'discussion_ongoing' | 'closed_ignored' | 'assigned_externally' | 'reopened' | 'assignment_drifted' | 'cancelled';
  // ISO 8601 creation timestamp. Empty for legacy threads whose creation
  // time was never recorded.
  createdAt: string;
  // ISO 8601 timestamp of the last message. Empty for legacy threads
  // saved before the timestamp was recorded.
  lastMessageAt: string;
}

export interface ThreadMessage {
  role: 'user' | 'assistant';
  content: string;
  sender?: string;
  // ISO 8601 timestamp. Absent for messages saved before timestamps existed.
  ts?: string;
}

export interface ThreadDetail extends SchedulingThread {
  messages: ThreadMessage[];
}

export interface SendMessageResponse {
  response?: string;
  status: 'processing' | 'assigned' | 'discussion_ongoing' | 'closed_ignored' | 'assigned_externally' | 'reopened' | 'assignment_drifted' | 'cancelled';
}

export interface SchedulingResponse {
  threads: SchedulingThread[];
  total: number;
  pageSize: number;
  nextToken?: string;
  availableFilters?: {
    pillars: string[];
    managers: string[];
  };
}

async function apiCall<T>(idToken: string, method: string, path: string, body?: unknown): Promise<T> {
  const config = await getConfig();
  const resp = await fetch(config.apiUrl + path, {
    method,
    headers: {
      Authorization: `Bearer ${idToken}`,
      'Content-Type': 'application/json',
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  // Fail loudly on non-2xx so callers can distinguish a failed request from a
  // successful-but-empty result (e.g. a 500 must not read as "no overrides").
  // The backend returns { error } on failures; surface that as the message.
  if (!resp.ok) {
    let message = `Request failed (${resp.status})`;
    try {
      const data = await resp.json();
      if (data?.error) message = data.error;
    } catch {
      // Non-JSON error body — keep the status-based fallback message.
    }
    throw new Error(message);
  }
  return resp.json();
}

export async function getOverrides(idToken: string): Promise<OverridesResponse> {
  const data = await apiCall<{ overrides?: Override[]; currentUser?: CurrentUser }>(
    idToken,
    'GET',
    '/overrides',
  );
  return {
    overrides: data.overrides ?? [],
    currentUser: data.currentUser ?? { alias: '' },
  };
}

export async function updateOverride(
  idToken: string,
  alias: string,
  // directManager/pillar are PAPI-derived enrichment; lastModifiedAt/By are
  // stamped server-side — none of them are editable.
  body: Omit<
    Override,
    'testerAlias' | 'directManager' | 'pillar' | 'lastModifiedAt' | 'lastModifiedBy'
  >,
): Promise<void> {
  await apiCall(idToken, 'PUT', `/overrides/${encodeURIComponent(alias)}`, body);
}

// Create a new override for a tester the backend validates against PAPI (real
// alias, reports to a pillar manager, not already present). Throws an Error
// whose message is the backend's reason on any non-2xx response (see
// apiCall), so the caller can show it inline.
export async function createOverride(idToken: string, alias: string, comment?: string): Promise<void> {
  await apiCall(idToken, 'POST', '/overrides', { testerAlias: alias, comment: comment ?? '' });
}

export async function getPrompts(idToken: string): Promise<Prompt[]> {
  const data = await apiCall<{ prompts?: Prompt[] }>(idToken, 'GET', '/prompts');
  return data.prompts ?? [];
}

export async function updatePrompt(idToken: string, key: string, content: string): Promise<void> {
  await apiCall(idToken, 'PUT', `/prompts/${encodeURIComponent(key)}`, { content });
}

export async function resetPrompt(idToken: string, key: string): Promise<void> {
  await apiCall(idToken, 'PUT', `/prompts/${encodeURIComponent(key)}`, { reset: true });
}

export async function getPromptHistory(idToken: string, key: string): Promise<PromptVersion[]> {
  const data = await apiCall<{ history?: PromptVersion[] }>(
    idToken,
    'GET',
    `/prompts/${encodeURIComponent(key)}/history`,
  );
  return data.history ?? [];
}

export async function getSchedulingThreads(
  idToken: string,
  opts?: { pageSize?: number; nextToken?: string; search?: string; hideAssigned?: boolean; sortBy?: string; sortOrder?: string; filterPillar?: string; filterManager?: string },
): Promise<SchedulingResponse> {
  const params = new URLSearchParams();
  if (opts?.pageSize) params.set('pageSize', String(opts.pageSize));
  if (opts?.nextToken) params.set('nextToken', opts.nextToken);
  if (opts?.search) params.set('search', opts.search);
  if (opts?.hideAssigned) params.set('hideAssigned', 'true');
  if (opts?.sortBy) params.set('sortBy', opts.sortBy);
  if (opts?.sortOrder) params.set('sortOrder', opts.sortOrder);
  if (opts?.filterPillar) params.set('filterPillar', opts.filterPillar);
  if (opts?.filterManager) params.set('filterManager', opts.filterManager);
  const qs = params.toString();
  return apiCall<SchedulingResponse>(idToken, 'GET', `/scheduling${qs ? `?${qs}` : ''}`);
}

export async function getThread(idToken: string, threadUuid: string): Promise<ThreadDetail> {
  return apiCall<ThreadDetail>(idToken, 'GET', `/scheduling/${encodeURIComponent(threadUuid)}`);
}

export async function sendMessage(idToken: string, threadUuid: string, message: string): Promise<SendMessageResponse> {
  return apiCall<SendMessageResponse>(idToken, 'POST', `/scheduling/${encodeURIComponent(threadUuid)}/message`, { message });
}

export async function markThreadAssignedExternally(
  idToken: string,
  threadUuid: string,
  note?: string,
): Promise<{ status: string; threadStatus: string }> {
  return apiCall(idToken, 'POST', `/scheduling/${encodeURIComponent(threadUuid)}/mark-assigned`, { note: note ?? '' });
}

// Re-open a scheduled/closed thread so the conversation becomes interactive
// again (status flips to 'reopened'). Planning-only: this unlocks the ASPEN
// chat, it does NOT touch Playbook/PRT/SIM. Throws (via apiCall) on non-2xx.
export async function reopenThread(
  idToken: string,
  threadUuid: string,
  note?: string,
): Promise<{ status: string; threadStatus: string }> {
  return apiCall(idToken, 'POST', `/scheduling/${encodeURIComponent(threadUuid)}/reopen`, { note: note ?? '' });
}

// ── Adoption / usage stats ──

export interface StatsPerUser {
  alias: string;
  connections: number;
  interactions: number;
  dialogInteractions: number;
  schedulingInteractions: number;
  // ISO 8601 UTC timestamps of the user's first and last recorded event.
  firstSeen: string;
  lastSeen: string;
}

export interface StatsResponse {
  // ISO 8601 UTC timestamp of when the aggregation was computed.
  generatedAt: string;
  totals: {
    connections: number;
    interactions: number;
    // Distinct users with any event.
    uniqueUsers: number;
    // Distinct users with at least one interaction (engaged, not just visited).
    engagedUsers: number;
  };
  perUser: StatsPerUser[];
  connectionsOverTime: { date: string; connections: number; uniqueUsers: number }[];
  interactionsByKind: { dialog: number; scheduling: number };
  // Pentest-engagement metrics, derived from the LLMContexts threads.
  engagementTotals: {
    // Every pentest engagement that reached ASPEN (one per thread).
    engagementsReached: number;
    // Threads booked through ASPEN (status 'assigned').
    scheduledWithAspen: number;
    // Threads staffed outside ASPEN (status 'assigned_externally').
    assignedExternally: number;
    // Total PRT escalation events (reschedule + cancellation) across all threads.
    escalations: number;
    // Scheduled-with-ASPEN threads that had an initial proposal to score.
    proposalRelevanceScored: number;
    // Mean proposal-relevance (0..1) across scored threads; null when none.
    avgProposalRelevance: number | null;
  };
  // How much of ASPEN's initial proposal survived into the final booking, over
  // scheduled-with-ASPEN threads that had a proposal to evaluate.
  proposalRelevanceBreakdown: {
    acceptedStraight: number; // score == 1.0
    partiallyModified: number; // 0 < score < 1
    replaced: number; // score == 0
  };
  // Monthly time series. engagementsReached/scheduled/assignedExternally are
  // bucketed by the engagement's arrival month (createdAt); escalations by the
  // month each escalation event happened. avgProposalRelevance is the mean
  // relevance of tests scheduled that month (null when none were scored).
  byMonth: {
    month: string; // 'YYYY-MM'
    engagementsReached: number;
    scheduled: number;
    assignedExternally: number;
    escalations: number;
    proposalRelevanceScored: number;
    avgProposalRelevance: number | null;
  }[];
}

// Record that the signed-in user opened the app. Fire-and-forget: the backend
// attributes the connection to the verified Cognito identity (the body is
// ignored) and never returns an error, so failures here are swallowed by the
// caller. Any authenticated user may record their own connection.
export async function recordSession(idToken: string): Promise<void> {
  try {
    await apiCall(idToken, 'POST', '/stats/session');
  } catch {
    // Telemetry is best-effort; never surface a failure to the user.
  }
}

// Fetch the aggregated adoption dashboard. The backend restricts this to admins
// (Federate `is_admin` claim) and returns 403 for everyone else, which apiCall
// turns into a thrown Error the caller can render.
export async function getStats(idToken: string): Promise<StatsResponse> {
  return apiCall<StatsResponse>(idToken, 'GET', '/stats');
}

// ── "Help Me" read-only diagnostics chatbot ──

export interface DiagnosticMessage {
  role: 'user' | 'assistant';
  content: string;
  sender?: string;
  // ISO 8601 timestamp. Absent for messages saved before timestamps existed.
  ts?: string;
}

export interface DiagnosticSession {
  sessionId: string;
  createdAt?: string;
  lastMessageAt?: string;
  messages: DiagnosticMessage[];
}

export interface DiagnosticSendResponse {
  status: 'processing';
}

// Create a new diagnostic ("Help Me") chat session. Returns the new session id
// (and an empty message list).
export async function createDiagnosticSession(idToken: string): Promise<DiagnosticSession> {
  const data = await apiCall<{ sessionId: string; messages?: DiagnosticMessage[] }>(
    idToken,
    'POST',
    '/diagnostics',
  );
  return { sessionId: data.sessionId, messages: data.messages ?? [] };
}

// Fetch a diagnostic session (its flattened message transcript).
export async function getDiagnosticSession(idToken: string, sessionId: string): Promise<DiagnosticSession> {
  const data = await apiCall<DiagnosticSession>(
    idToken,
    'GET',
    `/diagnostics/${encodeURIComponent(sessionId)}`,
  );
  return { ...data, messages: data.messages ?? [] };
}

// Send a question to the diagnostic agent. The backend accepts it (202) and
// runs the read-only investigation asynchronously; poll getDiagnosticSession
// for the reply (same pattern as the scheduling chat).
export async function sendDiagnosticMessage(
  idToken: string,
  sessionId: string,
  message: string,
): Promise<DiagnosticSendResponse> {
  return apiCall<DiagnosticSendResponse>(
    idToken,
    'POST',
    `/diagnostics/${encodeURIComponent(sessionId)}/message`,
    { message },
  );
}
